import React from "react";

import { MultiLineChartMock, PieChartMock } from "@/__mock_data__/chart-mock";
import ChartCard from "@/components/dashboard/chart-card";
import LineChartGraph from "@/components/dashboard/line-chart-graph";
import PieChartGraph from "@/components/dashboard/pie-chart-graph";
import ChatLogsChart from "@/components/dashboard/chat-logs-chart";
import { ChartConfig } from "@/components/ui/chart";
import { DatePickerWithRange } from "@/components/ui/datepicker-with-range";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import getGreeting from "@/lib/utils";
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from "../ui/card";
import useOnboardingData from "@/store/onboarding-data";
import { useGetVisitorAnalyticsFull } from "@/api/stores/get-visitor-analytics-full";
import { useGetChatsGraph } from "@/api/chat/get-chats-graph";

const chartConfig = {
    desktop: {
        label: "Desktop",
        color: "#2563eb",
    },
    mobile: {
        label: "Mobile",
        color: "#60a5fa",
    },
} satisfies ChartConfig;


const pieChartConfig = {
    visitors: {
        label: "Visitors",
    },
    chrome: {
        label: "Chrome",
        color: "#FDC800",
    },
    safari: {
        label: "Safari",
        color: "#2D7FFF",
    },
    firefox: {
        label: "Firefox",
        color: "#FF8904",
    },
    edge: {
        label: "Edge",
        color: "#00B8DA",
    },
    other: {
        label: "Other",
        color: "#00A63E",
    },
} satisfies ChartConfig;

const deviceDistributionConfig = {
    desktop: {
        label: "Desktop",
        color: "#2563eb",
    },
    mobile: {
        label: "Mobile",
        color: "#60a5fa",
    },
    tablet: {
        label: "Tablet",
        color: "#8b5cf6",
    },
    other: {
        label: "Other",
        color: "#6ee7b7",
    },
} satisfies ChartConfig;

const customerTypeConfig = {
    new: {
        label: "New Customers",
        color: "#FDC800",
    },
    returning: {
        label: "Returning Customers",
        color: "#2D7FFF",
    },
} satisfies ChartConfig;

const proactiveEngagementConfig = {
    shown: {
        label: "Proactive Shown",
        color: "#FF8904",
    },
    accepted: {
        label: "Proactive Accepted",
        color: "#00B8DA",
    },
} satisfies ChartConfig;

const chatLogsConfig = {
    count: {
        label: "Chat Count",
        color: "#34A853",
    },
} satisfies ChartConfig;

export default function DashboardContainer() {
    const { storeData } = useOnboardingData();
    const { data: analyticsData, isLoading: analyticsLoading } = useGetVisitorAnalyticsFull(storeData?.id || "");
    const { data: chatLogsData, isLoading: chatLogsLoading } = useGetChatsGraph();

    const totalVisitors = React.useMemo(() => {
        return PieChartMock.reduce((acc, curr) => acc + curr.visitors, 0);
    }, []);

    const deviceDistributionData = React.useMemo(() => {
        if (!analyticsData?.data) {
            // Return mock data while loading or if no data
            return [
                { device: "desktop", percentage: 65, fill: "var(--color-desktop)" },
                { device: "mobile", percentage: 30, fill: "var(--color-mobile)" },
                { device: "tablet", percentage: 5, fill: "var(--color-tablet)" },
            ];
        }

        return [
            { device: "desktop", percentage: analyticsData.data.device_split.desktop_percentage, fill: "var(--color-desktop)" },
            { device: "mobile", percentage: analyticsData.data.device_split.mobile_percentage, fill: "var(--color-mobile)" },
            { device: "tablet", percentage: analyticsData.data.device_split.tablet_percentage, fill: "var(--color-tablet)" },
            { device: "other", percentage: analyticsData.data.device_split.other_percentage, fill: "var(--color-other)" },
        ];
    }, [analyticsData]);

    const customerTypeData = React.useMemo(() => {
        if (!analyticsData?.data) {
            return [
                { type: "new", percentage: 70, fill: "var(--color-new)" },
                { type: "returning", percentage: 30, fill: "var(--color-returning)" },
            ];
        }

        return [
            { type: "new", percentage: analyticsData.data.conversations.new_customer_percentage, fill: "var(--color-new)" },
            { type: "returning", percentage: analyticsData.data.conversations.returning_customer_percentage, fill: "var(--color-returning)" },
        ];
    }, [analyticsData]);

    const proactiveEngagementData = React.useMemo(() => {
        if (!analyticsData?.data) {
            return [
                { type: "shown", value: 15, fill: "var(--color-shown)" },
                { type: "accepted", value: 10, fill: "var(--color-accepted)" },
            ];
        }

        return [
            { type: "shown", value: analyticsData.data.engagement.proactive_shown, fill: "var(--color-shown)" },
            { type: "accepted", value: analyticsData.data.engagement.proactive_accepted, fill: "var(--color-accepted)" },
        ];
    }, [analyticsData]);

    const chatLogsChartData = React.useMemo(() => {
        if (!chatLogsData?.data || !Array.isArray(chatLogsData.data)) {
            // Return mock data while loading or if no data
            // For mock data, create realistic dates similar to the API response format
            const mockData = [];
            for (let i = 9; i >= 0; i--) {
                const date = new Date();
                date.setDate(date.getDate() - i);
                const dateString = date.toISOString().split("T")[0]; // YYYY-MM-DD format
                // Use a predictable pattern for mock data (e.g., low numbers with occasional higher values)
                const count = i % 7 === 0 ? 15 : i % 5 === 0 ? 8 : i % 3 === 0 ? 5 : 2;
                mockData.push({
                    date: dateString,
                    count: count,
                });
            }
            return mockData;
        }

        // Transform the API response to chart format
        return chatLogsData.data.map((item) => ({
            date: item.date,
            count: item.count,
        }));
    }, [chatLogsData]);
    return (
        <div>
            <div className="grid grid-cols-4 gap-3">
                <div className="col-span-4">
                    <h3 className="text-3xl font-bold text-gray-700">{getGreeting()}</h3>
                    <h6 className="text-sm text-gray-700">Welcome to Shoe AI Dashboard</h6>
                </div>

                <DatePickerWithRange className="col-span-4" />

                <div className="col-span-4 md:col-span-2 lg:col-span-1">
                    <Card className="@container/card">
                        <CardHeader>
                            <CardDescription>Total Visitors</CardDescription>
                            <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
                                {analyticsLoading ? "Loading..." : analyticsData?.data?.overview.total_visitors || 0}
                            </CardTitle>
                            {/* <CardAction>
                                {analyticsData?.data?.overview.engagement_rate !== undefined && (
                                    <Badge variant="outline">
                                        {analyticsData.data.overview.engagement_rate >= 0 ? <TrendingUp /> : <TrendingDown />}
                                        {Math.abs(analyticsData.data.overview.engagement_rate)}%
                                    </Badge>
                                )}
                            </CardAction> */}
                        </CardHeader>
                        <CardFooter className="flex-col items-start gap-1.5 text-sm">
                            {/* <div className="line-clamp-1 flex gap-2 font-medium">
                                {analyticsData?.data?.overview.engagement_rate !== undefined && analyticsData.data.overview.engagement_rate >= 0 ? (
                                    <>
                                        Trending {analyticsData.data.overview.engagement_rate > 0 ? "up" : "neutral"} this month
                                        {analyticsData.data.overview.engagement_rate > 0 && <TrendingUp className="size-4" />}
                                    </>
                                ) : (
                                    <>
                                        Trending down this month <TrendingDown className="size-4" />
                                    </>
                                )}
                            </div> */}
                            <div className="text-muted-foreground">Total visitors to your store</div>
                        </CardFooter>
                    </Card>
                </div>
                <div className="col-span-4 md:col-span-2 lg:col-span-1">
                    <Card className="@container/card">
                        <CardHeader>
                            <CardDescription>Unique Visitors</CardDescription>
                            <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
                                {analyticsLoading ? "Loading..." : analyticsData?.data?.overview.unique_visitors || 0}
                            </CardTitle>
                            {/* <CardAction>
                                <Badge variant="outline">
                                    <TrendingUp />
                                    +2.5%
                                </Badge>
                            </CardAction> */}
                        </CardHeader>
                        <CardFooter className="flex-col items-start gap-1.5 text-sm">
                            {/* <div className="line-clamp-1 flex gap-2 font-medium">
                                Trending up this month <TrendingUp className="size-4" />
                            </div> */}
                            <div className="text-muted-foreground">Unique visitors to your store</div>
                        </CardFooter>
                    </Card>
                </div>
                <div className="col-span-4 md:col-span-2 lg:col-span-1">
                    <Card className="@container/card">
                        <CardHeader>
                            <CardDescription>Total Conversations</CardDescription>
                            <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
                                {analyticsLoading ? "Loading..." : analyticsData?.data?.conversations.total_conversations || 0}
                            </CardTitle>
                            {/* <CardAction>
                                <Badge variant="outline">
                                    <TrendingUp />
                                    +15.2%
                                </Badge>
                            </CardAction> */}
                        </CardHeader>
                        <CardFooter className="flex-col items-start gap-1.5 text-sm">
                            {/* <div className="line-clamp-1 flex gap-2 font-medium">
                                Trending up this month <TrendingUp className="size-4" />
                            </div> */}
                            <div className="text-muted-foreground">Total conversations with visitors</div>
                        </CardFooter>
                    </Card>
                </div>
                <div className="col-span-4 md:col-span-2 lg:col-span-1">
                    <Card className="@container/card">
                        <CardHeader>
                            <CardDescription>Engagement Rate</CardDescription>
                            <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
                                {analyticsLoading ? "Loading..." : `${analyticsData?.data?.overview.engagement_rate || 0}%`}
                            </CardTitle>
                            {/* <CardAction>
                                <Badge variant="outline">
                                    <TrendingDown />
                                    -2.56%
                                </Badge>
                            </CardAction> */}
                        </CardHeader>
                        <CardFooter className="flex-col items-start gap-1.5 text-sm">
                            {/* <div className="line-clamp-1 flex gap-2 font-medium">
                                Trending down this month <TrendingDown className="size-4" />
                            </div> */}
                            <div className="text-muted-foreground">Visitor engagement with the chatbot</div>
                        </CardFooter>
                    </Card>
                </div>
                {/* <div className="col-span-4 lg:col-span-2">
                    <LineChartGraph title="Line Chart 1" chartConfig={chartConfig} chartData={MultiLineChartMock} />
                </div> */}
                {/* <div className="col-span-4 flex flex-col gap-3 lg:col-span-2">
                    <LineChartGraph
                        chartContainerClassname={"max-h-[200px]"}
                        title={"Bar Chart"}
                        value={50}
                        subtitle={"This is awesome"}
                        chartConfig={singleChartConfig}
                        chartData={SingleLineChartMock}
                    />

                    <div className="col-span-4 lg:col-span-2">
                        <PieChartGraph
                            title="Pie Chart"
                            chartConfig={pieChartConfig}
                            chartData={PieChartMock}
                            value={100}
                            subtitle="This is a pie chart"
                            datakey="visitors"
                            valuekey="browser"
                            centerValue={totalVisitors.toLocaleString()}
                            centerText="Total Visitors"
                        />
                    </div>
                </div> */}
                {/* <div className="col-span-4">
                    <ChartCard value={237} title="Total Reply Count">
                        <div className="px-7">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="font-bold text-gray-500">Page Name</TableHead>
                                        <TableHead className="font-bold text-gray-500">Page Link</TableHead>
                                        <TableHead className="font-bold text-gray-500">Reply Count</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    <TableRow>
                                        <TableCell className="font-semibold text-gray-700">Home</TableCell>
                                        <TableCell>
                                            <a href="#" target="_blank" className="font-semibold text-sky-600">
                                                {" "}
                                                https://shoe-assistant.ai
                                            </a>
                                        </TableCell>
                                        <TableCell className="font-semibold text-gray-700">{formatNumber(2450)}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell className="font-semibold text-gray-700">About Us</TableCell>
                                        <TableCell>
                                            <a href="#" target="_blank" className="font-semibold text-sky-600">
                                                {" "}
                                                https://shoe-assistant.ai/about-us
                                            </a>
                                        </TableCell>
                                        <TableCell className="font-semibold text-gray-700">{formatNumber(764)}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell className="font-semibold text-gray-700">Products</TableCell>
                                        <TableCell>
                                            <a href="#" target="_blank" className="font-semibold text-sky-600">
                                                {" "}
                                                https://shoe-assistant.ai/products
                                            </a>
                                        </TableCell>
                                        <TableCell className="font-semibold text-gray-700">{formatNumber(86545)}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell className="font-semibold text-gray-700">Contact</TableCell>
                                        <TableCell>
                                            <a href="#" target="_blank" className="font-semibold text-sky-600">
                                                {" "}
                                                https://shoe-assistant.ai/contact
                                            </a>
                                        </TableCell>
                                        <TableCell className="font-semibold text-gray-700">{formatNumber(754)}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell className="font-semibold text-gray-700">Privacy & Policy</TableCell>
                                        <TableCell>
                                            <a href="#" target="_blank" className="font-semibold text-sky-600">
                                                {" "}
                                                https://shoe-assistant.ai/privacy-policy
                                            </a>
                                        </TableCell>
                                        <TableCell className="font-semibold text-gray-700">{formatNumber(9532)}</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </div>
                    </ChartCard>
                </div> */}
            </div>
            <div className="grid grid-cols-5 gap-3 mt-4">
                <div className="col-span-5 md:col-span-2 lg:col-span-2 bg-white p-4 rounded-xl shadow-md border">
                    <Card className="@container/card shadow-none">
                        <CardHeader>
                            <CardDescription>Live Agent Support</CardDescription>
                            <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">2</CardTitle>
                        </CardHeader>
                    </Card>
                    <Card className="@container/card shadow-none mt-4">
                        <CardHeader>
                            <CardDescription>Pending Agent Request</CardDescription>
                            <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">8</CardTitle>
                        </CardHeader>
                    </Card>
                </div>
                <div className="col-span-3">
                    <ChartCard value={237} title="Lead Collection">
                        <div className="px-7">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="font-bold text-gray-500">Email</TableHead>
                                        <TableHead className="font-bold text-gray-500">Phone</TableHead>
                                        <TableHead className="font-bold text-gray-500">Name</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    <TableRow>
                                        <TableCell className="font-semibold text-gray-700">jhon-doe@email.com</TableCell>
                                        <TableCell>
                                            <a href="#" target="_blank" className="font-semibold text-sky-600">
                                                {" "}
                                                +965 1234 5678
                                            </a>
                                        </TableCell>
                                        <TableCell className="font-semibold text-gray-700">Jhon Doe</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell className="font-semibold text-gray-700">jenny-doe@email.com</TableCell>
                                        <TableCell>
                                            <a href="#" target="_blank" className="font-semibold text-sky-600">
                                                {" "}
                                                +965 7854 8966
                                            </a>
                                        </TableCell>
                                        <TableCell className="font-semibold text-gray-700">Jenny Doe</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell className="font-semibold text-gray-700">jhon-doe@email.com</TableCell>
                                        <TableCell>
                                            <a href="#" target="_blank" className="font-semibold text-sky-600">
                                                {" "}
                                                +965 1234 5678
                                            </a>
                                        </TableCell>
                                        <TableCell className="font-semibold text-gray-700">Jhon Doe</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell className="font-semibold text-gray-700">jhon-doe@email.com</TableCell>
                                        <TableCell>
                                            <a href="#" target="_blank" className="font-semibold text-sky-600">
                                                {" "}
                                                +965 1234 5678
                                            </a>
                                        </TableCell>
                                        <TableCell className="font-semibold text-gray-700">Jhon Doe</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell className="font-semibold text-gray-700">jhon-doe@email.com</TableCell>
                                        <TableCell>
                                            <a href="#" target="_blank" className="font-semibold text-sky-600">
                                                {" "}
                                                +965 1234 5678
                                            </a>
                                        </TableCell>
                                        <TableCell className="font-semibold text-gray-700">Jhon Doe</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </div>
                    </ChartCard>
                </div>
                <div className="col-span-4 lg:col-span-2">
                    <PieChartGraph
                        title="Browser Distribution"
                        chartConfig={pieChartConfig}
                        chartData={PieChartMock}
                        value={100}
                        subtitle="Browser distribution"
                        datakey="visitors"
                        valuekey="browser"
                        centerValue={totalVisitors.toLocaleString()}
                        centerText="Total Visitors"
                    />
                </div>
                <div className="col-span-4 flex flex-col gap-3 lg:col-span-3">
                    <ChatLogsChart
                        chartContainerClassname={"max-h-[250px]"}
                        title={"Total Chats Over Time"}
                        value={
                            chatLogsData?.data && Array.isArray(chatLogsData.data)
                                ? chatLogsData.data.reduce((sum, item) => sum + (item.count || 0), 0)
                                : 0
                        }
                        subtitle={"Chat volume trend analysis"}
                        chartConfig={chatLogsConfig}
                        chartData={chatLogsChartData}
                        isLoading={chatLogsLoading}
                    />
                </div>
            </div>
            <div className="grid grid-cols-5 gap-3 mt-4">
                <div className="col-span-5 lg:col-span-2">
                    <PieChartGraph
                        title="Device Distribution"
                        chartConfig={deviceDistributionConfig}
                        chartData={deviceDistributionData}
                        value={100}
                        subtitle="Visitors by device type"
                        datakey="percentage"
                        valuekey="device"
                        centerValue={`${analyticsData?.data?.overview.total_visitors || 0}`}
                        centerText="Total Visitors"
                    />
                </div>
                <div className="col-span-5 lg:col-span-3">
                    <PieChartGraph
                        title="Customer Types"
                        chartConfig={customerTypeConfig}
                        chartData={customerTypeData}
                        value={100}
                        subtitle="New vs returning customers"
                        datakey="percentage"
                        valuekey="type"
                        centerValue={`${analyticsData?.data?.conversations.new_customers || 0} / ${
                            analyticsData?.data?.conversations.returning_customers || 0
                        }`}
                        centerText="New / Returning"
                    />
                </div>
            </div>

            <div className="grid grid-cols-5 gap-3 mt-4">
                <div className="col-span-5 lg:col-span-2">
                    <PieChartGraph
                        title="Proactive Engagement"
                        chartConfig={proactiveEngagementConfig}
                        chartData={proactiveEngagementData}
                        value={100}
                        subtitle="Proactive chat interactions"
                        datakey="value"
                        valuekey="type"
                        centerValue={`${analyticsData?.data?.engagement.proactive_engagement_rate || 0}%`}
                        centerText="Engagement Rate"
                    />
                </div>
                <div className="col-span-5 lg:col-span-3">
                    <LineChartGraph title="Engagement Trends" chartConfig={chartConfig} chartData={MultiLineChartMock} />
                </div>
            </div>
        </div>
    );
}
